import React, { Suspense, useMemo } from 'react'

import { useEditor } from '@toasttab/sites-components'
import classnames from 'classnames'

import {
  BackgroundImageFit,
  BaseSectionFieldsFragment,
  PaddingEnum,
  SpacingEnum
} from 'src/apollo/sites'
import { getImageUrl } from 'src/shared/components/common/Image'
import { getImageOverlayStyles } from 'src/shared/components/common/Image'
import AttributionContextProvider from 'src/shared/components/common/attribution_context/AttributionContext'
import { ScreenWidth, useIsMobile } from 'src/shared/js/utils/WindowContext'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'

import { resources } from 'config'

const spacingEnumToPaddingEnum = (spacing: SpacingEnum): PaddingEnum => {
  const map: Record<SpacingEnum, PaddingEnum> = {
    [SpacingEnum.None]: PaddingEnum.None,
    [SpacingEnum.Sm]: PaddingEnum.Small,
    [SpacingEnum.Md]: PaddingEnum.Medium,
    [SpacingEnum.Lg]: PaddingEnum.Large
  }
  return map[spacing] || PaddingEnum.None
}

/**
 * A section's own padding wins, then the site's vertical spacing, then MEDIUM.
 */
export const resolveVerticalSpacing = (
  sectionPadding: PaddingEnum | null | undefined,
  siteVerticalSpacing: SpacingEnum | null | undefined
): PaddingEnum => {
  if (sectionPadding) {
    return sectionPadding
  }
  if (siteVerticalSpacing) {
    return spacingEnumToPaddingEnum(siteVerticalSpacing)
  }
  return PaddingEnum.Medium
}

type BaseProps = {
  index: number
  testId?: string
  attributionUtmTerm: string
  className?: string
  editPath: string
}
type Props = BaseProps & Omit<BaseSectionFieldsFragment, '__typename'>

const getBackgroundStyle = (
  backgroundImageFit: BackgroundImageFit | null | undefined,
  isMobile?: boolean
) => {
  // for mobile, swap fit/fill
  if (isMobile) {
    switch (backgroundImageFit) {
      case BackgroundImageFit.Fit:
        return getBackgroundStyle(BackgroundImageFit.Fill)
      case BackgroundImageFit.Fill:
        return getBackgroundStyle(BackgroundImageFit.Fit)
      case BackgroundImageFit.Center:
      case BackgroundImageFit.Stretch:
      case BackgroundImageFit.Tile:
        return getBackgroundStyle(backgroundImageFit)
      // default to Fit
      default:
        return getBackgroundStyle(BackgroundImageFit.Fit)
    }
  }
  switch (backgroundImageFit) {
    // fits image to screen height, cropped sides
    case BackgroundImageFit.Fit:
      return {
        backgroundSize: 'auto 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center'
      }
    // fits image by changing aspect ratio, no cropping
    case BackgroundImageFit.Stretch:
      return {
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat'
      }
    case BackgroundImageFit.Tile:
      return { backgroundRepeat: 'repeat' }
    case BackgroundImageFit.Center:
      return {
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center'
      }
    // default - fill
    // fits image to screen width, cropped top/bottom
    default:
      return {
        backgroundSize: '100% auto',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center'
      }
  }
}

const SectionWrapper = ({
  children,
  index,
  testId,
  attributionUtmTerm,
  className,
  editPath,
  ...section
}: React.PropsWithChildren<Props>) => {
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const { restaurant } = useRestaurant()

  const backgroundColorStyle = useMemo(
    () =>
      section.backgroundColor
        ? { backgroundColor: section.backgroundColor }
        : {},
    [section.backgroundColor]
  )
  const backgroundImage = useMemo(() => {
    const backgroundImage = section.backgroundImage
    if (!backgroundImage) {
      return null
    }
    if (!backgroundImage?.src && !backgroundImage?.displaySrc) {
      return null
    }
    return backgroundImage
  }, [section.backgroundImage])
  const backgroundImageStyle = useMemo(() => {
    if (!backgroundImage) {
      return {}
    }

    const backgroundImageFit = backgroundImage?.backgroundImageFit
    let backgroundStyle = getBackgroundStyle(backgroundImageFit, isMobile)

    return {
      ...backgroundStyle,
      backgroundImage: `url(${getImageUrl(
        backgroundImage?.displaySrc || backgroundImage?.src,
        backgroundImage?.displaySrc
          ? resources.imagePrefix
          : resources.publicAssetURL
      )})`
    }
  }, [backgroundImage, isMobile])
  const overlayStyles = useMemo(() => {
    return getImageOverlayStyles(backgroundImage?.overlay)
  }, [backgroundImage?.overlay])
  const padding = useMemo(
    () =>
      resolveVerticalSpacing(
        section.padding,
        restaurant?.config?.designConfig?.verticalSpacing
      ),
    [section.padding, restaurant?.config?.designConfig?.verticalSpacing]
  )
  const { moduleWrapper } = useEditor()

  const WrapperComponent = useMemo(
    () =>
      moduleWrapper ||
      (({ children }: React.PropsWithChildren<{ index: number }>) => (
        <>{children}</>
      )),
    [moduleWrapper]
  )

  return (
    <WrapperComponent index={index}>
      <AttributionContextProvider utm_term={attributionUtmTerm}>
        <div
          id={`sectionIdx${index}`}
          className={classnames(
            'sectionWrapper bottomPadding topPadding',
            `PADDING-SETTING--${padding}`,
            className
          )}
          style={{
            ...(index === 0 ? { paddingTop: '0px' } : {}),
            position: 'relative',
            ...backgroundColorStyle,
            ...backgroundImageStyle
          }}
          data-testid={testId}
        >
          {backgroundImage?.overlay?.show && (
            <div
              data-testid='styledImageOverlay'
              className={'styledImageOverlay'}
              style={{ ...overlayStyles }}
            ></div>
          )}
          <div id={`${editPath}-add`} className='absolute top-0 left-0'></div>
          <div
            id={`${editPath}-edit`}
            className='absolute top-0 '
            style={{ right: '0px' }}
          />
          <div className='isolate'>
            <Suspense>{children}</Suspense>
          </div>
        </div>
      </AttributionContextProvider>
    </WrapperComponent>
  )
}

export default SectionWrapper
